<script setup>
/*
* This is a simple checkbox component that can be used in forms.
* It accepts a label, error message, input ID and whether the input is required.
* The input is styled with Tailwind CSS.
* When an error is present, a red border is displayed around the input and a red exclamation triangle is displayed to the right of the input.
*
* @param {String} label - The label for the input.
* @param {String} error - The error message to display.
* @param {String} inputId - The ID for the input.
* @param {String} type - The type of input.
* @param {Boolean} required - Whether the input is required.
*/

import { ref, watch } from 'vue';
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot, } from '@headlessui/vue';
import { Switch } from '@headlessui/vue';
import Button from './Button.vue';

const props = defineProps({
    label: String,
    error: String,
    inputId: String,
    analytics: {
        type: Boolean,
        default: false,
    },
    analyticsFunction: {
        type: Function,
        default: () => {},
    },
    marketing: {
        type: Boolean,
        default: false,
    },
    marketingFunction: {
        type: Function,
        default: () => {},
    },
    preferences: {
        type: Boolean,
        default: false,
    },
    preferencesFunction: {
        type: Function,
        default: () => {},
    },
});

const showCookie = ref(false)
const showEssential = ref(false)
const analyticsAccepted = ref(false)
const analyticsStorageAccepted = ref(false)
const adPersonalizationAccepted = ref(false)


//check if cookies need to be shown
function getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
}
function setCookie(name, value, expiry) {
    const expires = new Date();
    expires.setFullYear(expires.getFullYear() + (expiry === '1y' ? 1 : 0));
    document.cookie = `${name}=${value}; expires=${expires.toUTCString()}; path=/; SameSite=None; Secure`;
}
function removeCookie(name) {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}
function removeConsentCookie() {
    document.cookie = 'cookie-consent=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=None; Secure';
}

function acceptCookies() {
    setCookie('cookie-consent', 'true', '1y');
    showCookie.value = false;

    if (analyticsAccepted.value) {
        setCookie('cookie-analytics', 'true', '1y');
    } else{
        removeCookie('cookie-analytics');
    }
    if (analyticsStorageAccepted.value) {
        setCookie('cookie-analytics-storage', 'true', '1y');
    } else{
        removeCookie('cookie-analytics-storage');
    }
    if (adPersonalizationAccepted.value) {
        setCookie('cookie-ad-personalization', 'true', '1y');
    } else{
        removeCookie('cookie-ad-personalization');
    }

    setCookiesAsScripts();
}

function acceptAllCookies() {
    setCookie('cookie-consent', 'true', '1y');
    setCookie('cookie-analytics', 'true', '1y');
    setCookie('cookie-analytics-storage', 'true', '1y');
    setCookie('cookie-ad-personalization', 'true', '1y');

    setCookiesAsScripts();
    showCookie.value = false;
}

function setCookiesAsScripts() {
    analyticsAccepted.value = getCookie('cookie-analytics') === 'true';
    analyticsStorageAccepted.value = getCookie('cookie-analytics-storage') === 'true';
    adPersonalizationAccepted.value = getCookie('cookie-ad-personalization') === 'true';

    // Run every prop function here
    props.analyticsFunction(analyticsAccepted);
    props.marketingFunction(analyticsStorageAccepted);
    props.preferencesFunction(adPersonalizationAccepted);
}

function checkCookies() {
    // Check initial cookie consent state
    if(getCookie('cookie-consent') === null) {
        showCookie.value = true;
        return;
    }

    if (getCookie('cookie-analytics') !== null ||
        getCookie('cookie-analytics-storage') !== null ||
        getCookie('cookie-ad-personalization') !== null) {

        setCookiesAsScripts();
    }
}
checkCookies();


</script>

<template>
    <TransitionRoot as="template" :show="showCookie">
        <Dialog as="div" class="relative z-50">
            <div class="fixed inset-0 bg-black bg-opacity-50"></div>
            <div class="fixed left-0 right-0 top-0 bottom-0 z-[100] w-screen overflow-y-auto">
                <div class="flex h-full items-end justify-center p-2 sm:p-4 text-center sm:items-center">
                    <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                        <DialogPanel class="relative transform overflow-clip rounded-lg bg-white border shadow-3xl text-left transition-all sm:my-8 w-full sm:max-w-2xl">
                            <div class="flex flex-col gap-4 overflow-y-auto w-full max-h-[512px] sm:max-h-[628px] md:max-h-[745px] p-4">
                                <DialogTitle>
                                    <img class="h-10 sm:h-12 md:h-14 w-auto" src="/public/img/logo.webp" alt="Zwagers-automobielen" />
                                </DialogTitle>
                                <form action="#">
                                    <div>
                                        <h4 class="mb-2 text-wrap">Cookie instellingen</h4>
                                        <p class="mb-4 text-sm md:text-base text-balance">Om deze Diensten te gebruiken, hebben we uw toestemming nodig. Door op “Alles accepteren” te klikken, verklaart u uw toestemming voor het gebruik van alle Diensten. U kunt ook uw toestemming verklaren door individueel op de schuifregelaars voor elke categorie cookies te klikken en opslaan.</p>
                                    </div>
                                    <div class="pt-4 border-t">
                                        <h5 class="mb-2 text-wrap">Essentiele cookies
                                        </h5>
                                        <p class="mb-4 text-sm md:text-base text-balance">Essentiële cookies worden gebruikt om de basisfunctionaliteiten van de website te waarborgen, zoals navigatie en toegang tot beveiligde gedeelten van de website. Zonder deze cookies kan de website niet naar behoren functioneren.</p>
                                        <Button v-if="!showEssential" class="secondary-black-button ml-auto mb-4 !outline-none" @click="showEssential = true">Cookies bekijken</Button>
                                        <div v-if="showEssential" class="transition-all duration-300 ease-in-out my-4 w-full">
                                            <table class="w-full rounded-t-lg overflow-hidden">
                                                <thead class="w-full bg-tan/80 text-white text-left text-sm font-semibold">
                                                    <tr>
                                                        <th scope="col" class="table-cell px-1.5 sm:px-3 py-2.5 sm:py-3.5">Naam</th>
                                                        <th scope="col" class="table-cell px-1.5 sm:px-3 py-2.5 sm:py-3.5">Verlooptijd</th>
                                                        <th scope="col" class="table-cell px-1.5 sm:px-3 py-2.5 sm:py-3.5 w-full">Doel</th>
                                                    </tr>
                                                </thead>
                                                <tbody class="divide-y text-xs sm:text-sm">
                                                    <tr class="border-x">
                                                        <td class="whitespace-nowrap table-cell px-1.5 sm:px-3 py-3 sm:py-4">zwagers automotive_session</td>
                                                        <td class="whitespace-nowrap table-cell px-1.5 sm:px-3 py-3 sm:py-4">2 uur</td>
                                                        <td class="whitespace-nowrap table-cell px-1.5 sm:px-3 py-3 sm:py-4 w-full">Zorgt dat je dat data veilig verstuurd word</td>
                                                    </tr>
                                                    <tr class="border-x">
                                                        <td class="whitespace-nowrap table-cell px-1.5 sm:px-3 py-3 sm:py-4">XSRF-TOKEN</td>
                                                        <td class="whitespace-nowrap table-cell px-1.5 sm:px-3 py-3 sm:py-4">2 uur</td>
                                                        <td class="whitespace-nowrap table-cell px-1.5 sm:px-3 py-3 sm:py-4 w-full">Zorgt voor een naadloze ervaring</td>
                                                    </tr>
                                                    <tr class="border-x">
                                                        <td class="whitespace-nowrap table-cell px-1.5 sm:px-3 py-3 sm:py-4 border-b">fontsCssCache</td>
                                                        <td class="whitespace-nowrap table-cell px-1.5 sm:px-3 py-3 sm:py-4 border-b">7 dagen</td>
                                                        <td class="whitespace-nowrap table-cell px-1.5 sm:px-3 py-3 sm:py-4 w-full border-b">Geeft aan of de fonts al ingeladen zijn</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                        <Button v-if="showEssential" class="secondary-black-button ml-auto mb-4" @click="showEssential = false">Cookies verbergen</Button>
                                    </div>
                                    <div class="pt-4 border-t grid grid-cols-12 gap-y-2" v-if="analytics">
                                        <h5 class="text-left col-span-10">Analytische / statistische cookies</h5>
                                        <div class="flex justify-end col-span-2">
                                            <Switch v-model="analyticsAccepted" class="group relative inline-flex h-5 w-10 flex-shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none m-1">
                                                <span class="sr-only">Use setting</span>
                                                <span aria-hidden="true" class="pointer-events-none absolute h-full w-full rounded-md" />
                                                <span aria-hidden="true" :class="[analyticsAccepted ? 'bg-tan' : 'bg-gray-200', 'pointer-events-none absolute mx-auto h-6 w-12 rounded-full transition-colors duration-200 ease-in-out']" />
                                                <span aria-hidden="true" :class="[analyticsAccepted ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none absolute left-0 inline-block h-5 w-5 transform rounded-full border border-gray-200 bg-white shadow ring-0 transition-transform duration-200 ease-in-out']" />
                                            </Switch>
                                        </div>
                                        <p class="col-span-full mb-4 text-sm md:text-base text-balance">Analytics cookies worden gebruikt om informatie te verzamelen over hoe bezoekers onze website gebruiken. Deze informatie helpt ons om de website te verbeteren door inzicht te krijgen in het gebruik en de prestaties van de site.</p>
                                    </div>
                                    <div class="pt-4 border-t grid grid-cols-12 gap-y-2" v-if="marketing">
                                        <h5 class="text-left col-span-10">Marketingcookies</h5>
                                        <div class="flex justify-end col-span-2">
                                            <Switch v-model="analyticsStorageAccepted" class="group relative inline-flex h-5 w-10 flex-shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none m-1">
                                                <span class="sr-only">Use setting</span>
                                                <span aria-hidden="true" class="pointer-events-none absolute h-full w-full rounded-md" />
                                                <span aria-hidden="true" :class="[analyticsStorageAccepted ? 'bg-tan' : 'bg-gray-200', 'pointer-events-none absolute mx-auto h-6 w-12 rounded-full transition-colors duration-200 ease-in-out']" />
                                                <span aria-hidden="true" :class="[analyticsStorageAccepted ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none absolute left-0 inline-block h-5 w-5 transform rounded-full border border-gray-200 bg-white shadow ring-0 transition-transform duration-200 ease-in-out']" />
                                            </Switch>
                                        </div>
                                        <p class="col-span-full mb-4 text-sm md:text-base text-balance">Marketingcookies worden gebruikt om informatie te verzamelen over hoe bezoekers onze website gebruiken en wie ze zijn (gepersonaliseerd). Deze informatie helpt ons om de website te verbeteren door inzicht te krijgen in het gebruik en de prestaties van de site.</p>
                                    </div>
                                    <div class="pt-4 border-t grid grid-cols-12 gap-y-2" v-if="preferences">
                                        <h5 class="text-left col-span-10">Voorkeurscookies</h5>
                                        <div class="flex justify-end col-span-2">
                                            <Switch v-model="adPersonalizationAccepted" class="group relative inline-flex h-5 w-10 flex-shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none m-1">
                                                <span class="sr-only">Use setting</span>
                                                <span aria-hidden="true" class="pointer-events-none absolute h-full w-full rounded-md" />
                                                <span aria-hidden="true" :class="[adPersonalizationAccepted ? 'bg-tan' : 'bg-gray-200', 'pointer-events-none absolute mx-auto h-6 w-12 rounded-full transition-colors duration-200 ease-in-out']" />
                                                <span aria-hidden="true" :class="[adPersonalizationAccepted ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none absolute left-0 inline-block h-5 w-5 transform rounded-full border border-gray-200 bg-white shadow ring-0 transition-transform duration-200 ease-in-out']" />
                                            </Switch>
                                        </div>
                                        <p class="col-span-full mb-4 text-sm md:text-base text-balance">Cookies die onthouden welke keuzes een gebruiker maakt (bijvoorbeeld, welke taal je hebt geselecteerd) </p>
                                    </div>
                                </form>
                            </div>
                                <div class="sticky bottom-0 z-10 bg-white flex flex-wrap p-4 justify-end items-center gap-2 border-t">
                                    <Button @click="acceptCookies" class="secondary-black-button">Opslaan en verder</Button>
                                    <Button class="primary-button" @click="acceptAllCookies">Alles accepteren</Button>
                                </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>
